import { useState } from "react";
import { Phone, Mail, Instagram, Linkedin, CheckCircle2 } from "lucide-react";
import { usePage, useSettings } from "@/lib/content";
import { Reveal } from "@/components/site/Motion";
import { PageHero, usePageTitle } from "@/components/site/Bits";
import api, { formatApiErrorDetail } from "@/lib/api";

const inputCls =
  "w-full border border-navy/20 bg-ivory/40 px-4 py-3.5 text-sm text-navy placeholder:text-navy/35 transition-colors focus:border-gold";

export default function Contato() {
  usePageTitle("Contato");
  const { sections: s } = usePage("contato");
  const { settings } = useSettings();
  const c = settings?.contact || {};

  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [apiError, setApiError] = useState("");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const validate = () => {
    const er = {};
    if (form.name.trim().length < 3) er.name = "Informe seu nome.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) er.email = "Informe um e-mail válido.";
    if (form.phone.replace(/\D/g, "").length < 8) er.phone = "Informe um telefone válido.";
    if (form.subject.trim().length < 3) er.subject = "Informe o assunto.";
    if (form.message.trim().length < 10) er.message = "Escreva sua mensagem (mínimo 10 caracteres).";
    return er;
  };

  const submit = async (e) => {
    e.preventDefault();
    setApiError("");
    const er = validate();
    setErrors(er);
    if (Object.keys(er).length) return;
    setSending(true);
    try {
      await api.post("/contact", form);
      setDone(true);
    } catch (err) {
      setApiError(formatApiErrorDetail(err.response?.data?.detail) || "Não foi possível enviar. Tente novamente.");
    } finally {
      setSending(false);
    }
  };

  const channels = [
    c.phone && {
      icon: <Phone size={20} strokeWidth={1.5} />,
      label: "Telefone e WhatsApp",
      value: c.phone,
      href: `https://wa.me/${c.whatsapp || c.phone.replace(/\D/g, "")}`,
      testid: "contato-canal-whatsapp",
      external: true,
    },
    c.email && {
      icon: <Mail size={20} strokeWidth={1.5} />,
      label: "E-mail",
      value: c.email,
      href: `mailto:${c.email}`,
      testid: "contato-canal-email",
    },
    c.instagram && {
      icon: <Instagram size={20} strokeWidth={1.5} />,
      label: "Instagram",
      value: c.instagram_handle || "@fernandacotan",
      href: c.instagram,
      testid: "contato-canal-instagram",
      external: true,
    },
    c.linkedin && {
      icon: <Linkedin size={20} strokeWidth={1.5} />,
      label: "LinkedIn",
      value: "Fernanda Cotan",
      href: c.linkedin,
      testid: "contato-canal-linkedin",
      external: true,
    },
  ].filter(Boolean);

  return (
    <>
      <PageHero title={s.hero_title || "Contato"} text={s.hero_text} overline="Silva Cotan e Associados — Assessoria Jurídica" />

      <section className="bg-ivory">
        <div className="mx-auto max-w-7xl px-5 py-20 md:px-8 md:py-28">
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {channels.map((ch, i) => (
              <Reveal key={ch.testid} delay={0.06 * i}>
                <a
                  href={ch.href}
                  target={ch.external ? "_blank" : undefined}
                  rel={ch.external ? "noopener noreferrer" : undefined}
                  data-testid={ch.testid}
                  className="group flex h-full flex-col justify-between border border-navy/10 bg-white p-8 transition-all duration-300 hover:-translate-y-1 hover:border-gold"
                >
                  <span className="text-gold-deep">{ch.icon}</span>
                  <span className="mt-8">
                    <span className="block text-[10px] font-bold uppercase tracking-[0.22em] text-inkgray/60">{ch.label}</span>
                    <span className="mt-2 block text-sm font-semibold text-navy group-hover:text-gold-deep break-all">{ch.value}</span>
                  </span>
                </a>
              </Reveal>
            ))}
          </div>

          <div className="mt-20 grid gap-12 lg:grid-cols-12">
            <div className="lg:col-span-4">
              <Reveal>
                <h2 className="font-display text-3xl text-navy md:text-4xl">{s.form_title || "Envie sua mensagem"}</h2>
                <div className="gold-rule my-6 w-16" aria-hidden="true" />
                <p className="text-sm leading-relaxed text-inkgray md:text-base">{s.form_text}</p>
              </Reveal>
            </div>
            <div className="lg:col-span-8">
              {done ? (
                <Reveal>
                  <div className="flex h-full flex-col items-center justify-center border border-gold/40 bg-white px-8 py-20 text-center" data-testid="contato-success">
                    <CheckCircle2 size={44} className="text-gold-deep" strokeWidth={1.25} />
                    <p className="mt-6 font-display text-2xl text-navy md:text-3xl">Mensagem enviada com sucesso</p>
                    <p className="mt-4 max-w-md text-sm leading-relaxed text-inkgray">
                      Sua mensagem foi recebida e registrada. Retornaremos o mais breve possível pelo canal informado.
                    </p>
                  </div>
                </Reveal>
              ) : (
                <Reveal delay={0.1}>
                  <form onSubmit={submit} noValidate className="border border-navy/10 bg-white p-8 md:p-10" data-testid="contato-form">
                    <div className="grid gap-6 sm:grid-cols-2">
                      <F label="Nome *">
                        <input className={inputCls} value={form.name} onChange={set("name")} placeholder="Seu nome" data-testid="contato-nome" />
                        {errors.name && <p className="mt-1.5 text-xs text-red-600">{errors.name}</p>}
                      </F>
                      <F label="E-mail *">
                        <input type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="voce@email.com" data-testid="contato-email" />
                        {errors.email && <p className="mt-1.5 text-xs text-red-600">{errors.email}</p>}
                      </F>
                      <F label="Telefone *">
                        <input className={inputCls} value={form.phone} onChange={set("phone")} placeholder="(00) 00000-0000" data-testid="contato-telefone" />
                        {errors.phone && <p className="mt-1.5 text-xs text-red-600">{errors.phone}</p>}
                      </F>
                      <F label="Assunto *">
                        <input className={inputCls} value={form.subject} onChange={set("subject")} placeholder="Assunto da mensagem" data-testid="contato-assunto" />
                        {errors.subject && <p className="mt-1.5 text-xs text-red-600">{errors.subject}</p>}
                      </F>
                    </div>
                    <div className="mt-6">
                      <F label="Mensagem *">
                        <textarea rows={5} className={inputCls} value={form.message} onChange={set("message")} placeholder="Escreva sua mensagem" data-testid="contato-mensagem" />
                        {errors.message && <p className="mt-1.5 text-xs text-red-600">{errors.message}</p>}
                      </F>
                    </div>
                    {apiError && <p className="mt-4 border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="contato-error">{apiError}</p>}
                    <button
                      type="submit"
                      disabled={sending}
                      data-testid="contato-submit"
                      className="mt-8 inline-flex items-center gap-3 bg-navy px-8 py-4 text-[11px] font-bold uppercase tracking-[0.22em] text-ivory transition-colors hover:bg-navy-2 disabled:opacity-60"
                    >
                      {sending ? "Enviando…" : "Enviar mensagem"}
                    </button>
                  </form>
                </Reveal>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

function F({ label, children }) {
  return (
    <div>
      <label className="mb-2 block text-[11px] font-bold uppercase tracking-[0.18em] text-navy">{label}</label>
      {children}
    </div>
  );
}
